// Fine-grained slot classifier for the EditorContext projection — dispatcher.
// PRD: GRA-8 (MSSQL projection pivot), design §2.2. G11: pure text scan,
// NO web-tree-sitter — this is the legal projection-side body the future
// tree-sitter swap replaces behind the identical signature.
//
// Contract: for mssql, `resolveSlotKind` consults the source ONLY when the
// text clause machine left an ambiguous arm (`other` / `subquery` /
// `insert-values` / null); for any confidently-named clause it returns
// `'none'` immediately, so the slot refines an ambiguous position but never
// overrides a named one. For the four flip engines (postgres / mysql / oracle
// / sqlite, plan19 T7) the shared token DDL family (`classifyDdlSlot`)
// additionally runs on EVERY clause arm — it replicates the retired
// resolver-side `classifyDdlPositionKind` override, which fired regardless of
// clause; their per-engine RESIDUAL recognizers stay ambiguity-gated.
// Internals are surface-keyword tail scans over the tokenised statement slice.
//
// GRA8-ROLL-1 split the recognizers out of this file:
//   - editorContextSlot.mssql.ts      → T-SQL `data-type` shapes (per-engine)
//   - editorContextSlot.windowFrame.ts → dialect-neutral window-frame slot
//   - editorContextSlot.ddl.ts        → shared DDL constraint/reference slots
//   - editorContextSlotShared.ts      → token-scan primitives
// The per-engine data-type recognizer is dispatched on the `engine` param;
// only the mssql arm is wired at this slice (pgsql/mysql/oracle/sqlite get
// their own `isXxxDataTypeSlot` recognizers + resolver branches in later
// slices, so they return `'none'` here and stay byte-identical).

import type { EditorContextSlot, EditorContextSlotKind, SqlClause } from '../model/editorContext';
import { tokenizeUntil } from './editorContextClause';
import { ddlConstraintSlot } from './editorContextSlot.ddl';
import {
  classifyDdlSlot,
  DDL_FAMILY_SQL_ENGINES,
  indexColumnListSlot,
} from './editorContextSlot.ddl2';
import { indexOrPartitionColumnListSlot } from './editorContextSlot.ddl2.paren';
import { createObjectNameSlot } from './editorContextSlot.ddl2.createName';
import { triggerEventSlot } from './editorContextSlot.ddl2.trigger';
import { classifyGraphSlot, GRAPH_TABLE_ENGINES } from './editorContextSlot.graph';
import { isMssqlDataTypeSlot } from './editorContextSlot.mssql';
import { recognizePredicateHead } from './editorContextSlot.predicateHead';
import {
  mayBeRowSourceColumns,
  rowSourceColumnTypeSlot,
} from './editorContextSlot.rowSourceColumns';
import { recognizeValueSlot } from './editorContextSlot.value';
import {
  isMysqlCreateTableExpressionSlot,
  isMysqlDataTypeSlot,
  isMysqlFulltextSpatialIndexColumnSlot,
} from './editorContextSlot.mysql';
import { isOracleIndexExpressionSlot } from './editorContextSlot.oracle';
import {
  isAlterTableColumnTargetSlot,
  isDataTypeTargetSlot,
  isMssqlAlterColumnTypeSlot,
} from './editorContextSlotShared';
import { windowFrameSlot } from './editorContextSlot.windowFrame';

// Re-exported so the resolver + tests keep importing the window-frame detector
// from `editorContextSlot` after the split (it now lives in the sibling file).
export {
  detectMssqlWindowFrameSlot,
  type MssqlWindowFrameSlot,
} from './editorContextSlot.windowFrame';

// Clauses where the text machine is confident — the slot gate never runs.
// Everything NOT in this set (i.e. `other`, `subquery`, `insert-values`, and
// the `null` no-clause case) is an ambiguous arm the slot may refine.
const AMBIGUOUS_CLAUSES: ReadonlySet<SqlClause> = new Set<SqlClause>([
  'other',
  'subquery',
  'insert-values',
]);

function isAmbiguousArm(clause: SqlClause | null): boolean {
  return clause === null || AMBIGUOUS_CLAUSES.has(clause);
}

export type ResolveSlotArgs = {
  readonly source: string;
  readonly stmtStart: number;
  readonly offset: number;
  readonly engine: string;
  readonly clause: SqlClause | null;
};

// Value-slot plan T5a (D3): the discriminated entry point. The named
// recognizers run FIRST and keep every existing classification byte-identical;
// the scalar-value recognizer only fills positions that were 'none' before, so
// no named slot ever loses to it. Clause/engine gating of the scalar-value
// result is intellisense-owned (T5b capability gate) — this layer is
// syntax-only. Predicate-head plan P2 (PH-D7): the predicate-head scan runs
// LAST, only where the scalar-value scan returned nothing, so the two payload
// slots stay disjoint by construction on top of their corpus-pinned mutual
// refusals.
export function resolveSlot(args: ResolveSlotArgs): EditorContextSlot {
  const named = classifyNamedSlot(args);
  if (named !== 'none') return { kind: named };
  const valueSlot = recognizeValueSlot(args);
  if (valueSlot !== undefined) return { kind: 'scalar-value', valueSlot };
  const predicateHead = recognizePredicateHead(args);
  if (predicateHead !== undefined) return { kind: 'predicate-head', predicateHead };
  return { kind: 'none' };
}

// Bare-kind compatibility wrapper (D3/F4) for the projection caller
// (`editorContextProjection.ts`) and `deriveContext.ts` — both consume only
// the kind; payload consumers move to `resolveSlot`.
export function resolveSlotKind(args: ResolveSlotArgs): EditorContextSlotKind {
  return resolveSlot(args).kind;
}

function classifyNamedSlot(
  args: ResolveSlotArgs,
): Exclude<EditorContextSlotKind, 'scalar-value' | 'predicate-head'> {
  const { source, stmtStart, offset, engine, clause } = args;

  // Row-source COLUMNS type slot — `JSON_TABLE(… COLUMNS (tag ▮ PATH …))`,
  // `XMLTABLE(… COLUMNS (n ▮ …))`, T-SQL `OPENJSON(…) WITH (n ▮ '$.x')`.
  // Runs BEFORE both gates below because every one of these carets sits in a
  // NAMED `from` clause: the DDL family's override does not cover the shape
  // and the ambiguity gate can never reach it. Behind a cheap text pre-filter
  // (the row-source function name must appear before the caret) so the hot
  // path does not grow a tokenize pass — same technique as the mssql INDEX
  // pre-gate below. Dialect-neutral: an engine without the syntax simply
  // never matches.
  if (mayBeRowSourceColumns(source.slice(stmtStart, offset))) {
    const rowSourceTokens = tokenizeUntil(source, stmtStart, offset, engine);
    if (rowSourceTokens.length > 0 && rowSourceColumnTypeSlot(rowSourceTokens, offset)) {
      return 'row-source-column-type';
    }
  }

  // plan19 T7 — the four flip engines (postgres / mysql / oracle / sqlite)
  // route the full cross-engine token DDL family (`classifyDdlSlot`) through
  // the seam. It replicates the retired resolver-side
  // `classifyDdlPositionKind` override, which fired on EVERY clause arm (the
  // text machine lumps DDL slots under `other` / `unknown` / `from` /
  // `insert-columns` — and `CREATE INDEX … ON t USING gin (▮` even lands in
  // `join-on` via the USING keyword), so the family runs BEFORE the ambiguity
  // gate. It recognizes only CREATE/ALTER DDL + ON CONFLICT shapes and returns
  // null everywhere else, so named DML positions are untouched by
  // construction. mssql keeps its narrower ambiguity-gated pilot behavior.
  // Membership lives in DDL_FAMILY_SQL_ENGINES (editorContextSlot.ddl2.ts) —
  // the single source shared with deriveContext's DDL-override gate (Q4).
  const ddlFamilyEngine = DDL_FAMILY_SQL_ENGINES.has(engine);
  if (!ddlFamilyEngine && !isAmbiguousArm(clause)) {
    // mssql PRE-GATE — exactly one shape, `CREATE … INDEX … ON tbl (▮` and its
    // `INCLUDE (▮` continuation. T-SQL writes the index target after `ON`, so
    // the clause machine names the position 'from' / 'join-on' and mssql's
    // ambiguity gate can NEVER reach it, which left the index's column list
    // answering with the table list (script.31 §3-§6) while the other four
    // engines get the same shape through the family override above. Behind a
    // cheap text pre-filter so the hot path (ordinary DML at a named clause)
    // does not grow a tokenize pass.
    if (engine !== 'mssql') return 'none';
    if (!/\bINDEX\b/i.test(source.slice(stmtStart, offset))) return 'none';
    const indexTokens = tokenizeUntil(source, stmtStart, offset, engine);
    return indexTokens.length > 0 && indexColumnListSlot(indexTokens)
      ? 'ddl-index-column'
      : 'none';
  }

  const tokens = tokenizeUntil(source, stmtStart, offset, engine);
  if (tokens.length === 0) return 'none';

  if (ddlFamilyEngine) {
    const ddl = classifyDdlSlot(tokens, offset);
    if (ddl !== null) return ddl;
    // SQL/PGQ GRAPH_TABLE slots (O2) — runs BEFORE the ambiguity gate like the
    // DDL family, because the clause machine assigns a named clause
    // ('from'/'where') inside `GRAPH_TABLE( … )`, so a gated recognizer would
    // never see it. Gated to the engines whose grammar has GRAPH_TABLE
    // (oracle + postgres, both ⊆ DDL_FAMILY_SQL_ENGINES); returns null for any
    // statement without an enclosing `GRAPH_TABLE (`, so non-PGQ SQL is
    // byte-identical. The graph and DDL shapes are disjoint (a GRAPH_TABLE
    // SELECT has no REFERENCES/INDEX/CONSTRAINT/TRIGGER lead), so order is safe.
    if (GRAPH_TABLE_ENGINES.has(engine)) {
      const graph = classifyGraphSlot(tokens, offset);
      if (graph !== null) return graph.kind;
    }
    // The per-engine RESIDUAL recognizers below keep the original ambiguity
    // gate — they refine ambiguous arms only, never a named clause.
    if (!isAmbiguousArm(clause)) return 'none';
  }

  // The recognizers are dispatched per-engine. mssql was the pilot; each
  // additional engine gets its own arm as its recognizer + resolver branch
  // lands, so engines are independently controlled (the arm not wired returns
  // `'none'` and stays byte-identical).
  if (engine === 'mssql') {
    if (isMssqlDataTypeSlot(tokens, offset)) return 'data-type';
    if (windowFrameSlot(tokens) !== null) return 'window-frame';
    // The index column-list / INCLUDE-list recognizer, for the carets whose
    // clause IS ambiguous (the named-clause carets are answered by the
    // pre-gate above). Same shared recognizer either way.
    if (indexColumnListSlot(tokens)) return 'ddl-index-column';

    const ddl = ddlConstraintSlot(tokens);
    if (ddl !== null) return ddl;

    // The `PARTITION BY (▮` / bare `INDEX|KEY [name] (▮` openers. mssql never
    // calls `classifyDdlSlot`, so the shared entry point is dispatched here to
    // keep the answer identical across the five engines — T-SQL 2014+ accepts
    // the inline `INDEX ix (col)` form in a CREATE TABLE body.
    const opener = indexOrPartitionColumnListSlot(tokens);
    if (opener === 'ddl-create-table-expression') return opener;
    if (opener === 'ddl-alter-table-constraint-column-list') return opener;

    // `CREATE [OR ALTER] <object-type> ▮` — the NEW object's name slot. mssql
    // does not run `classifyDdlSlot` (it never consumed the retired
    // string-walk), so the shared recognizer is dispatched here to keep the
    // five engines' behavior identical at this slot. It stays inside mssql's
    // ambiguity gate, which is enough: the clause machine answers `other` for
    // every CREATE head, so the position reaches the unknown-gate.
    if (createObjectNameSlot(tokens, offset)) return 'ddl-create-object-name';

    // `CREATE TRIGGER t ON tbl AFTER ▮` — the DML event slot. mssql does not
    // run `classifyDdlSlot`, so only the narrow EVENT arm is dispatched here:
    // T-SQL puts the timing keyword AFTER the target table, which makes the
    // family's TIMING slot (fired right after the trigger NAME) wrong for this
    // dialect. See `editorContextSlot.ddl2.trigger.ts`.
    if (triggerEventSlot(tokens, offset)) return 'ddl-trigger-event';
  }

  // mysql (GRA8-ROLL-2): the two residual CREATE-TABLE in-progress-column
  // slots the DDL family does not cover — `PARTITION BY <method>(▮` and a bare
  // `INDEX`/`KEY (▮` secondary-index column list (FK/PK/UNIQUE/CHECK leads are
  // owned by `classifyDdlSlot` above).
  if (engine === 'mysql') {
    // GRA8-ROLL-4: the procedural-header data-type slot — `CREATE
    // PROCEDURE|FUNCTION (… <param> [IN|OUT|INOUT] ▮`. mysql had no data-type
    // emission before this slice, so the type slot fell to `unknown` and leaked
    // IN/OUT keywords + table names. Placed first (mirrors the mssql data-type
    // arm) so it claims the slot before the create-table-expression checks.
    if (isMysqlDataTypeSlot(tokens, offset)) return 'data-type';
    if (isMysqlCreateTableExpressionSlot(tokens)) {
      return 'ddl-create-table-expression';
    }
    // GRA8-ROLL-3: the two residual mysql index/ALTER target slots the DDL
    // family does not cover — FULLTEXT/SPATIAL index column-list and a bare
    // `DROP COLUMN` target. Plain/UNIQUE CREATE INDEX is owned by
    // `classifyDdlSlot` above (the recognizer returns false there).
    if (isMysqlFulltextSpatialIndexColumnSlot(tokens)) return 'ddl-index-column';
  }

  // GRA8-ROLL-6: the residual function-wrapped CREATE INDEX expression
  // slot the DDL family does not cover — `CREATE [UNIQUE|BITMAP] INDEX … ON
  // <tbl> ( … <fn>(▮ …)`. A function paren (e.g. `UPPER(`) becomes the LAST
  // unclosed `(`, so the direct `ON <tbl> (` recognizer sees the function paren
  // and returns null. Reuses `ddl-index-column` so the resolver emits the index
  // target's columns. The direct (non-function) index column list is owned by
  // `classifyDdlSlot` above (this recognizer returns false there — no
  // double-classify).
  //
  // CROSS-ENGINE (parity fix): the construct is not Oracle-specific — PG's
  // `CREATE INDEX … ON t ((lower(▮)))`, MySQL 8's functional keys and SQLite's
  // expression indexes are the same shape, and each engine's resolver already
  // routes `ddl-index-column` to "columns of the index target". T-SQL has no
  // expression indexes (only computed columns), so mssql never matches; the
  // recognizer is dialect-neutral and is dispatched for every engine. It lives
  // in the oracle recognizer file only because GRA8-ROLL-6 landed it there.
  if (isOracleIndexExpressionSlot(tokens)) return 'ddl-index-column';

  // sqlite (GRA8-ROLL-5): the residual `ALTER TABLE … RENAME COLUMN ▮`
  // existing-column slot the DDL family does not cover (no paren / REFERENCES /
  // trigger → null). Reuses `ddl-alter-table-constraint-column-list` as "live
  // columns of the ALTER target" so the sqlite resolver emits the altered
  // table's columns. `RENAME TO ▮` / `ADD COLUMN ▮` new-name slots stay
  // freeform (the recognizer returns false).
  // `ALTER TABLE <tbl> { DROP | ALTER | MODIFY | CHANGE } [COLUMN] ▮` and
  // `RENAME COLUMN ▮` — an EXISTING column is the target. The DDL family
  // returns null for all of them (no paren / REFERENCES / trigger), so without
  // this the caret reached the `unknown` arm and the altered table's own
  // columns never appeared. MySQL and SQLite each had a one-verb version of
  // this; the shared recognizer covers every engine's verbs.
  // Data-type positions every dialect shares (`CAST(x AS ▮`, a CREATE TABLE
  // column definition, and the ALTER TABLE column-type forms). Runs BEFORE the
  // column-name target below: `ADD col ▮` takes a type, `DROP COLUMN ▮` a name.
  if (isDataTypeTargetSlot(tokens, offset)) return 'data-type';

  // T-SQL's `ALTER TABLE t ALTER COLUMN <name> ▮` takes a data type directly
  // (PG needs a `TYPE` / `SET` / `DROP` keyword first, which is why the shared
  // recognizer above excludes the form).
  if (engine === 'mssql' && isMssqlAlterColumnTypeSlot(tokens, offset)) return 'data-type';

  if (isAlterTableColumnTargetSlot(tokens, offset)) {
    return 'ddl-alter-table-constraint-column-list';
  }

  return 'none';
}
